import React, { useCallback, useState } from 'react';
import SwiperType from 'swiper';
import Link from 'components/commercetools-ui/atoms/link';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { sanitize } from 'helpers/utils/htmlSanitize';
import { desktop, hugeDesktop, largeDesktop, mediumDesktop, mobile, tablet } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';

import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

/**
 * Pyxis — a section title/description above a horizontally scrolling strip of compact tiles, each
 * a tall icon over a short label linking to that card's first CTA. Maps the legacy
 * `card_header_button_section` category/brand navigation strip, where each item becomes a card.
 */
export default function PyxisRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, content_alignment, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'PyxisRendering');

  const textAlignmentClass =
    content_alignment === 'Center' ? 'text-center' :
    content_alignment === 'Right' ? 'text-right' :
    'text-left';

  //The strip runs edge to edge on smaller screens, so there is no gutter to hold arrows until the
  //section picks up its horizontal padding at `desktop`. Below that the slider is swiped instead.
  const [isDesktopSize] = useMediaQuery(desktop);

  //Swiper locks itself when every slide fits the track at once - fewer cards than `slidesPerView`
  //at the current breakpoint. There is nothing to page through in that state, so the arrows are
  //dropped and the short row is centred rather than left hanging against the start edge.
  //
  //The lock state is subscribed to on the instance rather than through `onLock`/`onUnlock` props:
  //swiper/react attaches prop-derived handlers in a later layout effect than the one that inits the
  //swiper, so the `lock` emitted during init lands before they exist and the strip stays stuck in
  //its unlocked render. `onSwiper` is forwarded by the Slider atom itself, so hooking up from there
  //catches both the value already on the instance and every later change. Swiper drops its own
  //listeners on destroy, so there is nothing to unsubscribe.
  const [isLocked, setIsLocked] = useState(false);
  const syncLocked = useCallback((swiper: SwiperType) => {
    const read = () => setIsLocked(!!swiper.isLocked);
    read();
    swiper.on('lock', read);
    swiper.on('unlock', read);
    swiper.on('resize', read);
    swiper.on('breakpoint', read);
  }, []);

  const arrowStyles = { top: '45%', transform: 'translateY(-50%)', zIndex: 10, position: 'absolute' as const };
  const showArrows = isDesktopSize && !isLocked;

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`product-section bg-white pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div className="pl-24 md:px-28 lg:px-48 xl:px-60 2xl:px-[61px] 3xl:px-80">
          {title && (
            <Typography
              as="h3"
              className={`mb-36 md:mb-48 text-32 xl:text-36 2xl:text-48 font-medium leading-8 xl:leading-[2.25rem] 2xl:leading-[3.25rem] text-[#090909] ${textAlignmentClass}`}
            >
              {title}
            </Typography>
          )}
          {description && (
            <div
              className={`mb-24 text-16 md:text-18 2xl:text-20 font-normal leading-6 text-[#333] ${textAlignmentClass}`}
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}
          {cards && cards.length > 0 && (
            <Slider
              allowTouchMove
              //Safe to paint server-side here: the tiles are content-sized, so sizing them to their
              //content rather than the atom's 300px default puts the server render close to the
              //final layout instead of overflowing the track. Full-bleed sliders must not opt in.
              visibleBeforeInit
              slideWidthIsFlexible
              className={isLocked ? styles.centered : ''}
              arrows={showArrows}
              dots={false}
              innerArrows={false}
              allowArrowsOnTouchDevice
              solidArrows
              onSwiper={syncLocked}
              prevButtonStyles={showArrows ? { ...arrowStyles, left: '-40px' } : {}}
              nextButtonStyles={showArrows ? { ...arrowStyles, right: '-40px' } : {}}
              slidesPerView={2.5}
              spaceBetween={16}
              breakpoints={{
                [mobile]: {
                  slidesPerView: 4.15,
                  spaceBetween: 16,
                },
                [tablet]: {
                  slidesPerView: 6,
                  spaceBetween: 16,
                },
                [desktop]: {
                  slidesPerView: 7,
                  spaceBetween: 16,
                },
                [mediumDesktop]: {
                  slidesPerView: 8,
                  spaceBetween: 16,
                },
                [largeDesktop]: {
                  slidesPerView: 9,
                  spaceBetween: 16,
                },
                [hugeDesktop]: {
                  slidesPerView: 9,
                  spaceBetween: 16,
                },
              }}
            >
              {cards.map((card: MediaCard, index: number) => {
                const image = card?.desktop_image;
                //Only the first CTA is used: the tile itself is the link, so a second one would
                //have nowhere to go.
                const cta = card?.ctas?.[0];

                const tile = (
                  //Production styles the tile through three classes that live in the Specify app's
                  //CSS bundle, not here: `.bg-color` (#E5E9EB), `.bd-r` (12px radius) and
                  //`.custom-hover:hover` (#E5E9EB). Carrying the bare class names over left the tile
                  //unstyled, since nothing in this repo defines them - so the same values are set as
                  //utilities instead. Grey up to `lg`, white above it, and grey again on hover: the
                  //`:hover` rule outranks the `lg` background on production, so above the breakpoint
                  //hovering returns the tile to grey. Below it the tile is already grey, which is why
                  //the hover only needs to exist from `lg` up.
                  <div className="flex flex-col items-center rounded-[12px] bg-[#E5E9EB] p-16 lg:bg-white lg:hover:bg-[#E5E9EB]">
                    {image?.url && (
                      <Image
                        loading="lazy"
                        src={image.url}
                        alt={image?.description ?? ''}
                        fill
                        style={{ objectFit: 'cover' }}
                        className="!relative !h-100 !w-[100px]"
                      />
                    )}
                    {card?.title && (
                      <Typography
                        as="h4"
                        //The label is reserved a fixed block of height so tiles with a one-line and
                        //a two-line label still line their icons up. Driven by a breakpoint rather
                        //than a measured width, so it is right in the server-rendered HTML too.
                        className="mt-12 flex min-h-[60px] flex-col text-ellipsis pt-12 text-center text-16 font-medium leading-[25px] text-[#090909] lg:min-h-[85px] lg:text-20 xl:text-18 2xl:text-22"
                      >
                        {card.title}
                      </Typography>
                    )}
                  </div>
                );

                //A card without a CTA is still a valid tile - wrapping it in a link would only
                //produce an anchor pointing at "#".
                return cta?.url ? (
                  <Link key={index} link={cta.url} target={cta?.target ?? '_self'} className="block">
                    {tile}
                  </Link>
                ) : (
                  <div key={index}>{tile}</div>
                );
              })}
            </Slider>
          )}
        </div>
      </div>
    </>
  );
}
